// src/components/PrivacyNoticeStep.jsx
import React, { useState } from "react";
import {
  Info,
  Database,
  Users,
  Share2,
  ShieldCheck,
  HelpCircle,
  User,
  Target,
  ArrowRight,
} from "lucide-react";
import { privacyNotice } from "./data/mockData";

const ICONS = {
  Info,
  Database,
  Users,
  Share2,
  ShieldCheck,
  HelpCircle,
  User,
  Target,
};

const CARD_TONES = {
  "who-we-are": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "data-we-collect": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "why-we-collect": { bg: "var(--green-50)", fg: "var(--green-600)" },
  "data-sharing": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "identity-data": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "technical-data": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "usage-data": { bg: "var(--green-50)", fg: "var(--green-600)" },
  "consent-records": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "service-provision": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "legal-compliance": { bg: "var(--green-50)", fg: "var(--green-600)" },
  "security-fraud": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "communication": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "service-providers": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "legal-authorities": { bg: "var(--green-50)", fg: "var(--green-600)" },
  "group-entities": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "right-access": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "right-correction": { bg: "var(--green-50)", fg: "var(--green-600)" },
  "right-withdraw": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "right-grievance": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "dpo-contact": { bg: "var(--blue-50)", fg: "var(--blue-700)" },
  "office-address": { bg: "var(--purple-50)", fg: "var(--purple-600)" },
  "response-timeline": { bg: "var(--green-50)", fg: "var(--green-600)" },
};

const SECTION_DETAILS = {
  overview: {
    title: "Overview",
    intro: privacyNotice?.overview?.intro || "Whitehats Technologies acts as a Data Fiduciary and is committed to protecting your personal data.",
    cards: privacyNotice?.overview?.cards || [
      { id: "who-we-are", title: "Who We Are", icon: "User", body: "Whitehats Technologies Pvt. Ltd. We are the Data Fiduciary responsible for your personal data." },
      { id: "data-we-collect", title: "Data We Collect", icon: "Database", body: "We collect personal, financial, identity, usage, and other data that you provide to us." },
      { id: "why-we-collect", title: "Why We Collect", icon: "Target", body: "We collect your data for specific purposes such as service delivery, compliance, security, and improvement." },
      { id: "data-sharing", title: "Data Sharing", icon: "Share2", body: "We may share your data with authorized partners, service providers, and regulatory authorities as required." },
    ],
    showRightsBox: true,
  },
  "data-we-collect": {
    title: "Data We Collect",
    intro: "We collect various categories of personal data to provide, optimize, and secure our services for you:",
    cards: [
      { id: "identity-data", title: "Identity & Profile Information", icon: "User", body: "Includes full name, email address, contact phone number, date of birth, identity proofs, and user role." },
      { id: "technical-data", title: "Technical & Device Data", icon: "Database", body: "Includes IP address, browser type and version, time zone setting, operating system, device identifiers, and access logs." },
      { id: "usage-data", title: "Usage & Preference Data", icon: "Target", body: "Includes information about how you interact with our platform, feature usage, activity history, and selected preferences." },
      { id: "consent-records", title: "Consent & Audit Records", icon: "ShieldCheck", body: "Includes timestamped consent declarations, specific purposes accepted/declined, channel of consent, and version history." },
    ],
    showRightsBox: false,
  },
  "why-we-collect": {
    title: "Why We Collect Your Data",
    intro: "Your personal data is processed strictly for specified, explicit, and legitimate business purposes:",
    cards: [
      { id: "service-provision", title: "Service Provision & Account Management", icon: "User", body: "To register your account, verify your identity, deliver core functionality, and provide customer support." },
      { id: "legal-compliance", title: "Regulatory & Legal Obligations", icon: "ShieldCheck", body: "To comply with statutory regulations (DPDP Act, GDPR, DORA), maintain audit trails, and respond to legal inquiries." },
      { id: "security-fraud", title: "Platform Security & Risk Mitigation", icon: "Target", body: "To prevent unauthorized access, detect fraudulent activities, ensure system integrity, and perform security audits." },
      { id: "communication", title: "Communication & Updates", icon: "Share2", body: "To send transactional notifications, privacy policy updates, security alerts, and respond to your requests." },
    ],
    showRightsBox: false,
  },
  "data-sharing": {
    title: "Data Sharing & Disclosure",
    intro: "We maintain strict data privacy standards. We do not sell your personal data. Sharing occurs only as detailed below:",
    cards: [
      { id: "service-providers", title: "Authorized Data Processors", icon: "Database", body: "Third-party cloud infrastructure, analytics providers, and communication gateways bound by data processing agreements." },
      { id: "legal-authorities", title: "Legal & Regulatory Authorities", icon: "ShieldCheck", body: "Disclosures mandated by applicable law, court orders, or statutory authorities to uphold legal compliance." },
      { id: "group-entities", title: "Corporate Restructuring & Affiliates", icon: "User", body: "In the event of a merger, acquisition, or internal restructuring, with continued adherence to this privacy notice." },
    ],
    showRightsBox: false,
  },
  "your-rights": {
    title: "Your Rights as a Data Principal",
    intro: "Under applicable Data Protection legislation (such as DPDP Act & GDPR), you hold statutory rights regarding your personal data:",
    cards: [
      { id: "right-access", title: "Right to Access & Summary", icon: "Info", body: "You can request a summary of personal data being processed and details of processing activities." },
      { id: "right-correction", title: "Right to Correction & Erasure", icon: "ShieldCheck", body: "You can request correction of inaccurate data or erasure of data no longer required for its original purpose." },
      { id: "right-withdraw", title: "Right to Withdraw Consent", icon: "Target", body: "You have the right to revoke your consent for optional processing activities at any time without penalty." },
      { id: "right-grievance", title: "Right to Grievance Redressal", icon: "HelpCircle", body: "You may submit inquiries or file grievances with our Data Protection Officer regarding data processing." },
    ],
    showRightsBox: true,
  },
  "contact-us": {
    title: "Contact Us & Grievance Redressal",
    intro: "If you have questions regarding this Privacy Notice or wish to exercise your data rights, reach out to our privacy team:",
    cards: [
      { id: "dpo-contact", title: "Data Protection Officer (DPO)", icon: "User", body: "Email: dpo@whitehats.in | Phone: +91 (0) 80-4950-0000 | Hours: Mon-Fri 9:00 AM - 6:00 PM IST" },
      { id: "office-address", title: "Grievance Redressal Office", icon: "HelpCircle", body: "Whitehats Technologies Pvt. Ltd., Privacy & Compliance Department, Tech Park, Bangalore, India." },
      { id: "response-timeline", title: "Response & Processing Timeline", icon: "Target", body: "All privacy queries and DSAR (Data Subject Access Requests) are acknowledged within 24 hours and fulfilled within 7 days." },
    ],
    showRightsBox: false,
  },
};

export default function PrivacyNoticeStep({ onContinue }) {
  const [activeSection, setActiveSection] = useState("overview");
  const sectionData = SECTION_DETAILS[activeSection] || SECTION_DETAILS["overview"];

  return (
    <section
      style={{
        background: "#fff",
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--gray-200)",
        boxShadow: "var(--shadow-sm)",
        width: "100%",
      }}
    >
      <div style={{ padding: "20px 28px 0" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--blue-700)",
            background: "var(--blue-50)",
            padding: "3px 9px",
            borderRadius: 5,
            letterSpacing: 0.3,
          }}
        >
          STEP 1 OF 4
        </span>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: "var(--gray-900)",
            margin: "10px 0 4px",
          }}
        >
          Privacy Notice
        </h2>
        <p style={{ fontSize: 13.5, color: "var(--gray-500)", margin: 0 }}>
          Please read the privacy notice carefully before proceeding
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: 24,
          padding: "20px 28px 28px",
          alignItems: "flex-start",
        }}
      >
        {/* Sidebar */}
        <nav
          style={{
            width: 190,
            flexShrink: 0,
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--radius-md)",
            overflow: "hidden",
          }}
        >
          {privacyNotice.sections.map((sec) => {
            const Icon = ICONS[sec.icon] || Info;
            const active = activeSection === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => setActiveSection(sec.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 14px",
                  border: "none",
                  borderBottom: "1px solid var(--gray-100)",
                  background: active ? "var(--blue-700)" : "#fff",
                  color: active ? "#fff" : "var(--gray-700)",
                  fontSize: 13.5,
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                <Icon size={15} />
                {sec.label}
              </button>
            );
          })}
        </nav>

        {/* Content */}
        <div
          style={{
            flex: 1,
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--radius-md)",
            padding: 22,
          }}
        >
          <h3
            style={{
              fontSize: 15.5,
              fontWeight: 700,
              color: "var(--gray-900)",
              margin: "0 0 6px",
            }}
          >
            {sectionData.title}
          </h3>
          <p
            style={{
              fontSize: 13.5,
              color: "var(--gray-600)",
              margin: "0 0 18px",
              lineHeight: 1.5,
            }}
          >
            {sectionData.intro}
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            {sectionData.cards.map((card) => {
              const Icon = ICONS[card.icon] || Info;
              const tone = CARD_TONES[card.id] || { bg: "var(--blue-50)", fg: "var(--blue-700)" };
              return (
                <div
                  key={card.id}
                  style={{
                    border: "1px solid var(--gray-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 16,
                    background: "var(--gray-25)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: tone.bg,
                        color: tone.fg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={16} />
                    </span>
                    <h4
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--gray-900)",
                        margin: 0,
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--gray-600)",
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {card.body}
                  </p>
                </div>
              );
            })}
          </div>

          {sectionData.showRightsBox && (
            <div
              style={{
                marginTop: 16,
                background: "var(--green-50)",
                border: "1px solid var(--green-200)",
                borderRadius: "var(--radius-md)",
                padding: "14px 16px",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <ShieldCheck
                size={17}
                style={{ color: "var(--green-600)", marginTop: 1, flexShrink: 0 }}
              />
              <div>
                <strong
                  style={{
                    fontSize: 13.5,
                    color: "var(--gray-900)",
                    display: "block",
                    marginBottom: 3,
                  }}
                >
                  Your Rights:
                </strong>
                <span style={{ fontSize: 13, color: "var(--gray-600)" }}>
                  {privacyNotice?.overview?.rights?.items ? privacyNotice.overview.rights.items.join("  •  ") : "Access your data  •  Correct inaccurate data  •  Request deletion  •  Withdraw consent  •  File a grievance  •  Submit DSAR"}
                </span>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: 20,
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={onContinue}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "var(--blue-700)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 18px",
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Continue to Purpose Selection
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
